import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { varianteService, Variante, VarianteCreate, VarianteUpdate } from '../../services/varianteService';
import { productoService } from '../../services/productoService';

interface VarianteFormProps {
  variante?: Variante | null;
  onClose: () => void;
}

const VarianteForm = ({ variante, onClose }: VarianteFormProps) => {
  const queryClient = useQueryClient();

  const { data: productosData } = useQuery(
    'productos',
    () => productoService.getAll(1, 200)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<VarianteCreate | VarianteUpdate>({
    defaultValues: variante ? {
      name: variante.name,
      sku: variante.sku,
      codigoBarras: variante.codigoBarras,
      precioAdicional: variante.precioAdicional
    } : {
      name: '',
      sku: '',
      codigoBarras: '',
      precioAdicional: 0
    }
  });

  const productoId = watch('productoId');
  const precioAdicional = watch('precioAdicional');
  const productoSeleccionado = productosData?.data.productos.find(p => p.id === productoId);

  useEffect(() => {
    if (variante) {
      reset({
        name: variante.name,
        sku: variante.sku,
        codigoBarras: variante.codigoBarras,
        precioAdicional: variante.precioAdicional
      });
    }
  }, [variante, reset]);

  const createMutation = useMutation(varianteService.create, {
    onSuccess: () => {
      toast.success('Variante creada correctamente');
      queryClient.invalidateQueries(['variantes']);
      queryClient.invalidateQueries(['productos']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear variante');
    }
  });

  const updateMutation = useMutation(
    (data: VarianteUpdate) => varianteService.update(variante!.id, data),
    {
      onSuccess: () => {
        toast.success('Variante actualizada correctamente');
        queryClient.invalidateQueries(['variantes']);
        queryClient.invalidateQueries(['productos']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar variante');
      }
    }
  );

  const onSubmit = (data: VarianteCreate | VarianteUpdate) => {
    if (!variante && !productoId) {
      toast.error('Debes seleccionar un producto');
      return;
    }

    if (variante) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate({
        ...data,
        productoId: productoId!
      } as VarianteCreate);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {variante ? 'Editar Variante' : 'Nueva Variante'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {!variante && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Producto *
              </label>
              <select
                {...register('productoId', { 
                  required: 'Producto es requerido',
                  valueAsNumber: true
                })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Selecciona un producto</option>
                {productosData?.data.productos.map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.name} {producto.sku ? `(${producto.sku})` : ''}
                  </option>
                ))}
              </select>
              {errors.productoId && (
                <p className="text-red-500 text-sm mt-1">{errors.productoId.message}</p>
              )}
            </div>
          )}

          {variante && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-600">
                <strong>Producto:</strong> {variante.producto?.name}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de la Variante *
            </label>
            <input
              type="text"
              {...register('name', { 
                required: 'Nombre es requerido',
                minLength: { value: 1, message: 'Mínimo 1 caracter' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Ej: Grande, Rojo, Talla M"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU
            </label>
            <input
              type="text"
              {...register('sku')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="SKU de la variante"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código de Barras
            </label>
            <input
              type="text"
              {...register('codigoBarras')}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Código de barras único"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio Adicional
            </label>
            <input
              type="number"
              step="0.01"
              {...register('precioAdicional', { 
                valueAsNumber: true,
                min: { value: -999999, message: 'Valor mínimo' }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="0.00"
            />
            {errors.precioAdicional && (
              <p className="text-red-500 text-sm mt-1">{errors.precioAdicional.message}</p>
            )}
            {productoSeleccionado && precioAdicional !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                Precio base: ${typeof productoSeleccionado.precioBase === 'number' ? productoSeleccionado.precioBase.toFixed(2) : (Number(productoSeleccionado.precioBase) || 0).toFixed(2)} | 
                Precio final: ${(Number(productoSeleccionado.precioBase) + Number(precioAdicional || 0)).toFixed(2)}
              </p>
            )}
          </div>

          {variante && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activa</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? 'Guardando...'
                : variante
                ? 'Actualizar'
                : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VarianteForm;

